<div class="topic-share-container">
  <header class="topic-share-header">
    <h1>📊 Topic Share</h1>
    <p>Discover the core keyword for any topic</p>
  </header>

  <div class="topic-share-section">
    <div class="form-container">
      <div class="form-group">
        <label for="topic-input">Topic:</label>
        <input
          id="topic-input"
          type="text"
          [(ngModel)]="topic"
          placeholder="e.g., project management"
          class="topic-input"
          [disabled]="loading"
          (keyup.enter)="discoverCoreKeyword()"
        />
      </div>

      <div class="defaults-indicator">
        <span class="indicator-icon">🌍</span>
        <span class="indicator-text">
          Location: United States ({{ defaultLocationCode }}) | Language: {{ defaultLanguageName }}
        </span>
      </div>

      <button 
        (click)="discoverCoreKeyword()" 
        [disabled]="loading || !topic.trim()"
        class="discover-button"
      >
        <span *ngIf="!loading">Discover ranking domains</span>
        <span *ngIf="loading">Discovering...</span>
      </button>
    </div>

    <div *ngIf="error" class="error-message">
      <strong>Error:</strong> {{ error }}
    </div>

    <div *ngIf="result && metadata" class="results-container">
      <div class="result-card">
        <div class="result-header">
          <h2>Core Keyword Result</h2>
          <div class="metadata-badge" [class.from-database]="metadata.dataSource === 'database'" [class.from-api]="metadata.dataSource === 'api'">
            <span class="badge-icon">{{ metadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ metadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="metadata.lastUpdated" class="badge-date">
              ({{ formatDate(metadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div class="result-content">
          <div class="result-item">
            <label>Topic:</label>
            <span class="result-value">{{ result.topic }}</span>
          </div>
          <div class="result-item">
            <label>Core Keyword:</label>
            <span class="result-value core-keyword">{{ result.coreKeyword }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- SERP Results Section -->
    <div *ngIf="loadingSerp" class="loading-indicator">
      <span>Loading ranking domains...</span>
    </div>

    <div *ngIf="serpResults.topic || serpResults.coreKeyword" class="serp-results-container">
      <div class="serp-results-header">
        <h2>Ranking Domains</h2>
        <div *ngIf="totalCost > 0" class="total-cost">
          Total Cost: ${{ totalCost.toFixed(4) }}
          <span *ngIf="serpCost > 0" class="cost-breakdown">
            (Core Keyword: ${{ coreKeywordCost.toFixed(4) }}, SERP: ${{ serpCost.toFixed(4) }})
          </span>
        </div>
      </div>
      
      <div class="serp-columns">
        <!-- Topic SERP Results - Left Column -->
        <div *ngIf="serpResults.topic" class="serp-column">
          <div class="serp-section">
            <div class="serp-header">
              <h3>For: "{{ searchTopic }}"</h3>
              <div *ngIf="serpMetadata.topic" class="metadata-badge" 
                   [class.from-database]="serpMetadata.topic.dataSource === 'database'" 
                   [class.from-api]="serpMetadata.topic.dataSource === 'api'">
                <span class="badge-icon">{{ serpMetadata.topic.dataSource === 'database' ? '💾' : '🌐' }}</span>
                <span class="badge-text">
                  {{ serpMetadata.topic.dataSource === 'database' ? 'From Database' : 'From API' }}
                </span>
                <span *ngIf="serpMetadata.topic.lastUpdated" class="badge-date">
                  ({{ formatDate(serpMetadata.topic.lastUpdated) }})
                </span>
              </div>
            </div>
            <div class="serp-list">
              <div *ngFor="let result of getDisplayedSerpResults('topic')" class="serp-item">
                <div class="serp-position">{{ result.position }}</div>
                <div class="serp-content">
                  <div class="serp-title">
                    <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.title }}</a>
                  </div>
                  <div class="serp-url">
                    <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.url }}</a>
                  </div>
                  <div class="serp-domain">{{ result.domain }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="hasMoreSerpResults('topic') && serpResults.topic" class="load-more-container">
              <button 
                (click)="toggleShowAllSerpResults('topic')" 
                class="load-more-button"
              >
                <span *ngIf="showAllSerpResults.topic">Show Less</span>
                <span *ngIf="!showAllSerpResults.topic">
                  Load More ({{ (serpResults.topic.results.length - serpDisplayLimit) }} more)
                </span>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Core Keyword SERP Results - Right Column -->
        <div *ngIf="serpResults.coreKeyword && !isTopicSameAsCoreKeyword" class="serp-column">
          <div class="serp-section">
            <div class="serp-header">
              <h3>For: "{{ result?.coreKeyword }}"</h3>
              <div *ngIf="serpMetadata.coreKeyword" class="metadata-badge" 
                   [class.from-database]="serpMetadata.coreKeyword.dataSource === 'database'" 
                   [class.from-api]="serpMetadata.coreKeyword.dataSource === 'api'">
                <span class="badge-icon">{{ serpMetadata.coreKeyword.dataSource === 'database' ? '💾' : '🌐' }}</span>
                <span class="badge-text">
                  {{ serpMetadata.coreKeyword.dataSource === 'database' ? 'From Database' : 'From API' }}
                </span>
                <span *ngIf="serpMetadata.coreKeyword.lastUpdated" class="badge-date">
                  ({{ formatDate(serpMetadata.coreKeyword.lastUpdated) }})
                </span>
              </div>
            </div>
            <div class="serp-list">
              <div *ngFor="let result of getDisplayedSerpResults('coreKeyword')" class="serp-item">
                <div class="serp-position">{{ result.position }}</div>
                <div class="serp-content">
                  <div class="serp-title">
                    <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.title }}</a>
                  </div>
                  <div class="serp-url">
                    <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.url }}</a>
                  </div>
                  <div class="serp-domain">{{ result.domain }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="hasMoreSerpResults('coreKeyword') && serpResults.coreKeyword" class="load-more-container">
              <button 
                (click)="toggleShowAllSerpResults('coreKeyword')" 
                class="load-more-button"
              >
                <span *ngIf="showAllSerpResults.coreKeyword">Show Less</span>
                <span *ngIf="!showAllSerpResults.coreKeyword">
                  Load More ({{ (serpResults.coreKeyword.results.length - serpDisplayLimit) }} more)
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Entity Discovery Section -->
    <div *ngIf="hasSerpResults" class="entity-discovery-container">
      <div class="entity-discovery-header">
        <h2>Entity Discovery</h2>
        <button 
          (click)="discoverEntities()" 
          [disabled]="loadingEntities || !hasSerpResults"
          class="discover-button"
        >
          <span *ngIf="!loadingEntities">Discover Entities</span>
          <span *ngIf="loadingEntities">Discovering entities...</span>
        </button>
      </div>

      <div *ngIf="loadingEntities" class="loading-indicator">
        <span>Discovering entities from related keywords, suggestions, and ranked keywords...</span>
      </div>

      <div *ngIf="urlsLimited && !loadingEntities" class="info-note warning-note">
        <div class="warning-header">
          <strong>⚠️ Limited to 10 SERP pages</strong>
          <span class="warning-count">({{ totalSerpUrls }} total available, {{ excludedUrls.length }} excluded)</span>
        </div>
        <p class="warning-description">Pages from topic results were prioritized. The following pages were excluded:</p>
        <div class="excluded-pages-list">
          <div *ngFor="let excluded of excludedUrls" class="excluded-page-item">
            <div class="excluded-page-header">
              <a [href]="excluded.url" target="_blank" rel="noopener noreferrer" class="excluded-page-title">
                {{ excluded.title }}
              </a>
              <span class="excluded-page-source" [class.source-topic]="excluded.source === 'topic'" [class.source-core]="excluded.source === 'coreKeyword'">
                {{ excluded.source === 'topic' ? 'Topic' : 'Core Keyword' }}
              </span>
            </div>
            <div class="excluded-page-url">
              <a [href]="excluded.url" target="_blank" rel="noopener noreferrer">{{ excluded.url }}</a>
            </div>
            <div class="excluded-page-domain">{{ excluded.domain }}</div>
          </div>
        </div>
      </div>

      <div *ngIf="entities.length > 0" class="entities-results">
        <!-- Cost Breakdown -->
        <div *ngIf="entityCostBreakdown" class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Related Keywords:</span>
              <span class="cost-value">${{ (entityCostBreakdown.relatedKeywords || 0).toFixed(4) }}</span>
            </div>
            <div class="cost-item">
              <span class="cost-label">Suggestions:</span>
              <span class="cost-value">${{ (entityCostBreakdown.suggestions || 0).toFixed(4) }}</span>
            </div>
            <div class="cost-item">
              <span class="cost-label">Ranked Keywords:</span>
              <span class="cost-value">${{ (entityCostBreakdown.rankedKeywords || 0).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ entityCostBreakdown.total.toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div *ngIf="entityStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Entities:</span>
              <span class="stat-value">{{ entityStats.totalEntities }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Related:</span>
              <span class="stat-value">{{ entityStats.fromRelated }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Suggestions:</span>
              <span class="stat-value">{{ entityStats.fromSuggestions }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Ranked Keywords:</span>
              <span class="stat-value">{{ entityStats.fromRankedKeywords }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Unique Entities:</span>
              <span class="stat-value">{{ entityStats.uniqueEntities }}</span>
            </div>
          </div>
        </div>

        <!-- Info Note -->
        <div class="info-note">
          <strong>Note:</strong> Ranked keywords are filtered by: min search volume >= 10, top 100 by position
        </div>

        <!-- Entities Table -->
        <div class="entities-table-container">
          <table class="entities-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Similarity Score</th>
                <th>Search Volume</th>
                <th>Keyword Difficulty</th>
                <th>Sources</th>
                <th>Ranked From Pages</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entity of getDisplayedEntities()">
                <td class="entity-keyword">
                  <strong>{{ entity.keyword }}</strong>
                  <span *ngIf="entity.coreKeyword && entity.coreKeyword !== entity.keyword" class="core-keyword-badge">
                    (core: {{ entity.coreKeyword }})
                  </span>
                </td>
                <td>
                  <span class="similarity-score" [class]="getSimilarityColorClass(entity.similarityScore)">
                    {{ formatSimilarity(entity.similarityScore) }}
                  </span>
                </td>
                <td>{{ entity.searchVolume.toLocaleString() }}</td>
                <td>{{ entity.keywordDifficulty }}</td>
                <td>
                  <div class="sources-badges">
                    <span *ngFor="let source of entity.sources" class="source-badge" [class]="'badge-' + source">
                      {{ source === 'related' ? 'Related' : source === 'suggestions' ? 'Suggestions' : 'Ranked' }}
                    </span>
                  </div>
                </td>
                <td class="ranked-pages">
                  <div *ngIf="getRankedFromPagesAsStrings(entity.rankedFromPages).length > 0" class="pages-list">
                    <a *ngFor="let url of getRankedFromPagesAsStrings(entity.rankedFromPages).slice(0, 3)" 
                       [href]="url" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       class="page-link">
                      {{ url }}
                    </a>
                    <span *ngIf="getRankedFromPagesAsStrings(entity.rankedFromPages).length > 3" class="more-pages">
                      +{{ getRankedFromPagesAsStrings(entity.rankedFromPages).length - 3 }} more
                    </span>
                  </div>
                  <span *ngIf="isRankedFromPagesEmpty(entity.rankedFromPages)" class="no-pages">-</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div *ngIf="hasMoreEntities()" class="load-more-container">
            <button 
              (click)="toggleShowAllEntities()" 
              class="load-more-button"
            >
              <span *ngIf="showAllEntities">Show Less</span>
              <span *ngIf="!showAllEntities">
                Load All ({{ entities.length - entitiesDisplayLimit }} more)
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Entity Filter Section -->
    <div *ngIf="entities.length > 0" class="entity-filter-container">
      <div class="entity-filter-header">
        <h2>Filter to Core Keywords</h2>
        <button 
          (click)="filterEntities()" 
          [disabled]="loadingFilter || !entities.length"
          class="filter-button"
        >
          <span *ngIf="!loadingFilter">Filter to Core Keywords</span>
          <span *ngIf="loadingFilter">Filtering...</span>
        </button>
      </div>

      <div *ngIf="loadingFilter" class="loading-indicator">
        <span>Filtering entities to core keywords...</span>
      </div>

      <div *ngIf="filteredCoreKeywords.length > 0" class="filter-results-container">
        <div class="filter-results-header">
          <h3>Filtered Core Keywords ({{ filteredCoreKeywords.length }})</h3>
        </div>

        <div class="filtered-keywords-list">
          <div *ngFor="let keyword of filteredCoreKeywords; let i = index" class="filtered-keyword-item">
            <div class="keyword-number">{{ i + 1 }}</div>
            <div class="keyword-content">
              <strong>{{ keyword }}</strong>
              <span *ngIf="filterDetails && i === 0" class="keyword-label">(Input Topic)</span>
              <span *ngIf="filterDetails && i === 1 && keyword !== filterDetails.inputTopic" class="keyword-label">(Core Keyword)</span>
              <span *ngIf="filterDetails && i >= (keyword === filterDetails.inputTopic ? 1 : 2)" class="keyword-label">
                (Similarity: {{ formatSimilarity(getAdditionalKeywordSimilarity(keyword)) }})
              </span>
            </div>
          </div>
        </div>

        <div *ngIf="filterDetails && filterDetails.additionalCoreKeywords.length > 0" class="selection-details">
          <h4>Selection Details</h4>
          <div class="details-list">
            <div *ngFor="let detail of filterDetails.additionalCoreKeywords" class="detail-item">
              <div class="detail-header">
                <strong>{{ detail.coreKeyword }}</strong>
                <span class="similarity-score" [class]="getSimilarityColorClass(detail.similarityScore)">
                  {{ formatSimilarity(detail.similarityScore) }}
                </span>
              </div>
              <div class="detail-content">
                <span>Best entity: <em>{{ detail.bestEntity }}</em></span>
                <span>•</span>
                <span>{{ detail.entityCount }} entity/entities</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtered Entity Discovery Section -->
    <div *ngIf="filteredCoreKeywords.length > 0" class="filtered-entity-discovery-container">
      <div class="filtered-entity-discovery-header">
        <h2>Discover More Entities + Ranking Pages</h2>
        <button 
          (click)="discoverFilteredEntities()" 
          [disabled]="loadingFilteredEntities || !filteredCoreKeywords.length"
          class="discover-button"
        >
          <span *ngIf="!loadingFilteredEntities">Discover More Entities + Ranking Pages</span>
          <span *ngIf="loadingFilteredEntities">Discovering...</span>
        </button>
      </div>

      <div *ngIf="loadingFilteredEntities" class="loading-indicator">
        <span>Discovering entities using filtered core keywords...</span>
      </div>

      <div *ngIf="filteredEntities.length > 0" class="filtered-entities-results">
        <!-- Cost Breakdown -->
        <div *ngIf="filteredEntityCostBreakdown" class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Ranked Keywords (Filtered):</span>
              <span class="cost-value">${{ (filteredEntityCostBreakdown.rankedKeywordsFiltered || 0).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ filteredEntityCostBreakdown.total.toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <!-- Statistics -->
        <div *ngIf="filteredEntityStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Entities:</span>
              <span class="stat-value">{{ filteredEntityStats.totalEntities.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Ranked Keywords (Filtered):</span>
              <span class="stat-value">{{ (filteredEntityStats.fromRankedKeywordsFiltered || 0).toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Unique Entities:</span>
              <span class="stat-value">{{ filteredEntityStats.uniqueEntities.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Domains Analyzed:</span>
              <span class="stat-value">{{ filteredEntityStats.domainsAnalyzed }}</span>
            </div>
          </div>
        </div>

        <!-- Domain Total Counts (API Response) -->
        <div *ngIf="hasDomainTotalCounts()" class="domain-total-counts-section">
          <h3>API Total Counts Per Domain</h3>
          <div class="domain-counts-info">
            <p class="info-text">These are the total matching keywords per domain from the API response (total_count). If a domain shows 1000 items but the total_count is higher, pagination would fetch more results. Note: When using cached data, this shows the total for all keywords (not just filtered).</p>
            <div class="domain-counts-list">
              <div *ngFor="let domainCount of getDomainCountsList()" class="domain-count-item">
                <span class="domain-name">{{ domainCount.domain }}</span>
                <span class="count-value">{{ domainCount.totalCount.toLocaleString() }}</span>
                <span *ngIf="domainCount.totalCount > 1000" class="pagination-needed">⚠️ Needs pagination</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Filtered Entities Table -->
        <div class="entities-table-container">
          <table class="entities-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Search Volume</th>
                <th>Keyword Difficulty</th>
                <th>Sources</th>
                <th>Ranking Pages</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entity of getDisplayedFilteredEntities()">
                <td class="entity-keyword">
                  <strong>{{ entity.keyword }}</strong>
                  <span *ngIf="entity.coreKeyword && entity.coreKeyword !== entity.keyword" class="core-keyword-badge">
                    (core: {{ entity.coreKeyword }})
                  </span>
                </td>
                <td>{{ entity.searchVolume.toLocaleString() }}</td>
                <td>{{ entity.keywordDifficulty }}</td>
                <td>
                  <div class="sources-badges">
                    <span *ngFor="let source of entity.sources" class="source-badge" [class]="'badge-' + source">
                      {{ source === 'related' ? 'Related' : source === 'suggestions' ? 'Suggestions' : source === 'ranked_keywords' ? 'Ranked' : 'Ranked (Filtered)' }}
                    </span>
                  </div>
                </td>
                <td class="ranked-pages">
                  <div *ngIf="isRankedFromPagesArray(entity.rankedFromPages) && getRankedFromPagesAsObjects(entity.rankedFromPages).length > 0" class="pages-list">
                    <div *ngFor="let page of getRankedFromPagesAsObjects(entity.rankedFromPages).slice(0, 3)" class="page-item">
                      <a [href]="page.url" target="_blank" rel="noopener noreferrer" class="page-link">
                        {{ page.title || page.url }}
                      </a>
                      <span class="page-meta">({{ page.domain }}, pos: {{ page.position }})</span>
                    </div>
                    <span *ngIf="getRankedFromPagesAsObjects(entity.rankedFromPages).length > 3" class="more-pages">
                      +{{ getRankedFromPagesAsObjects(entity.rankedFromPages).length - 3 }} more
                    </span>
                  </div>
                  <div *ngIf="!isRankedFromPagesArray(entity.rankedFromPages) && getRankedFromPagesAsStrings(entity.rankedFromPages).length > 0" class="pages-list">
                    <a *ngFor="let url of getRankedFromPagesAsStrings(entity.rankedFromPages).slice(0, 3)" 
                       [href]="url" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       class="page-link">
                      {{ url }}
                    </a>
                    <span *ngIf="getRankedFromPagesAsStrings(entity.rankedFromPages).length > 3" class="more-pages">
                      +{{ getRankedFromPagesAsStrings(entity.rankedFromPages).length - 3 }} more
                    </span>
                  </div>
                  <span *ngIf="isRankedFromPagesEmpty(entity.rankedFromPages)" class="no-pages">-</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div *ngIf="hasMoreFilteredEntities()" class="load-more-container">
            <button 
              (click)="toggleShowAllFilteredEntities()" 
              class="load-more-button"
            >
              <span *ngIf="showAllFilteredEntities">Show Less</span>
              <span *ngIf="!showAllFilteredEntities">
                Load All ({{ filteredEntities.length - entitiesDisplayLimit }} more)
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Domain Coverage Summary Section -->
    <div *ngIf="entities.length > 0 || filteredEntities.length > 0" class="domain-coverage-container">
      <div class="domain-coverage-header">
        <h2>Domain Coverage Summary</h2>
        <button 
          (click)="getDomainCoverage()" 
          [disabled]="loadingDomainCoverage || (entities.length === 0 && filteredEntities.length === 0)"
          class="coverage-button"
        >
          <span *ngIf="!loadingDomainCoverage">View Domain Coverage</span>
          <span *ngIf="loadingDomainCoverage">Calculating...</span>
        </button>
      </div>

      <div class="coverage-warning" *ngIf="domainCoverage.length > 0">
        <p class="warning-text">
          <strong>Note:</strong> This analysis is based on SERP data and filtered keyword discovery. 
          For complete coverage, a full site crawl is recommended. Some pages ranking for topic-related keywords may not be captured in this quick analysis.
        </p>
      </div>

      <div *ngIf="loadingDomainCoverage" class="loading-indicator">
        <span>Calculating domain coverage...</span>
      </div>

      <div *ngIf="domainCoverage.length > 0 && !loadingDomainCoverage" class="coverage-results">
        <div class="coverage-summary">
          <div class="summary-item">
            <span class="summary-label">Total Entities:</span>
            <span class="summary-value">{{ totalEntities.toLocaleString() }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Total URLs:</span>
            <span class="summary-value">{{ totalUrls.toLocaleString() }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Domains Analyzed:</span>
            <span class="summary-value">{{ domainCoverage.length }}</span>
          </div>
        </div>

        <div class="domains-table-container">
          <table class="domains-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Entities Ranked For</th>
                <th>URLs Ranking</th>
                <th>Ranking URLs</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let domain of domainCoverage">
                <td class="domain-name">
                  <strong>{{ domain.domain }}</strong>
                </td>
                <td class="entities-count">
                  <div class="entities-count-primary">
                    <span class="count-value" *ngIf="domain.apiTotalCount">{{ domain.apiTotalCount.toLocaleString() }}</span>
                    <span class="count-value" *ngIf="!domain.apiTotalCount">{{ domain.entitiesCount }}</span>
                    <span class="count-total">/ {{ totalEntities }}</span>
                    <span class="count-percentage">({{ ((domain.entitiesCount / totalEntities) * 100).toFixed(1) }}%)</span>
                  </div>
                  <div class="entities-count-note" *ngIf="domain.apiTotalCount && domain.apiTotalCount !== domain.entitiesCount">
                    <span class="note-text">
                      {{ domain.apiTotalCount.toLocaleString() }} keywords ranked for (API total_count) • 
                      {{ domain.entitiesCount }} discovered entities covered
                    </span>
                  </div>
                </td>
                <td class="urls-count">
                  <span class="count-value">{{ domain.urlsCount }}</span>
                </td>
                <td class="ranking-urls">
                  <div class="urls-list">
                    <div *ngFor="let urlDetail of domain.urls.slice(0, 5)" class="url-item">
                      <a [href]="urlDetail.url" target="_blank" rel="noopener noreferrer" class="url-link">
                        {{ urlDetail.title || urlDetail.url }}
                      </a>
                      <span class="url-meta">
                        <span class="url-position">(pos: {{ urlDetail.position }})</span>
                        <span class="url-entity-count" *ngIf="urlDetail.entityCount > 0">
                          • {{ urlDetail.entityCount }} {{ urlDetail.entityCount === 1 ? 'keyword' : 'keywords' }}
                        </span>
                      </span>
                    </div>
                    <div *ngIf="domain.urls.length > 5" class="more-urls">
                      +{{ domain.urls.length - 5 }} more URLs
                    </div>
                    <div *ngIf="domain.urls.length === 0" class="no-urls">-</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

